import { useState } from 'react';
import { PublishingMedium } from 'lib/enums/PublishingMedium';
import { LoadingSpinner } from 'lib/components/LoadingSpinner';
import { Alert } from 'lib/components/Alert';
import { GridInput } from 'lib/components/Card/Grid';
import { ColumnSelect } from 'lib/components/ColumnSelect';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { Product } from 'lib/enums';
import { PRODUCT_TO_NAME } from 'lib/enums/Product';
import { getModelFromSnapshot } from 'lib/model';
import { OrganizationModel } from 'lib/model/objects/organizationModel';
import { FilingTypeModel } from 'lib/model/objects/filingTypeModel';
import { supportsDisplay } from 'lib/notice/rates';
import {
  EOrganization,
  AdRate,
  ESnapshot,
  ESnapshotExists,
  ERef
} from 'lib/types';
import { ColumnService } from 'lib/services/directory';
import { AdRateService } from 'lib/services/adRateService';
import { getFirebaseContext } from 'utils/firebase';
import { usePublisherOrgRates } from '../../../routes/settings/publisher/rates/hooks/usePublisherOrgRates';
import { CustomerObjDataFields } from '../CreateOrEditModalDetails';

interface ProductRatesSettingsProps {
  productType: Product;
  value: CustomerObjDataFields;
  onChange: (value: CustomerObjDataFields) => void;
  activeOrganization: ESnapshotExists<EOrganization>;
}

interface FilingTypeRateConfigProps {
  filingType: FilingTypeModel;
  currentConfig?: {
    linerRate?: ERef<AdRate>;
    displayRate?: ERef<AdRate>;
  };
  availableRates: ESnapshotExists<AdRate>[];
  onUpdate: (
    rateType: 'liner' | 'display',
    rateSnap: ESnapshot<AdRate> | null
  ) => void;
}

function FilingTypeRateConfig({
  filingType,
  currentConfig,
  availableRates,
  onUpdate
}: FilingTypeRateConfigProps) {
  const linerRateSnap = availableRates.find(
    rate => rate.id === currentConfig?.linerRate?.id
  );
  const displayRateSnap = availableRates.find(
    rate => rate.id === currentConfig?.displayRate?.id
  );
  const linerRateIsArchived = !!linerRateSnap?.data()?.archived;
  const displayRateIsArchived = !!displayRateSnap?.data()?.archived;

  const linerRateOptions = availableRates
    ?.filter(rate => {
      if (currentConfig?.linerRate?.id === rate.id) return true;
      return !rate.data().archived;
    })
    .map(rate => ({
      value: rate.id,
      label: rate.data()?.description
    }));

  const displayRateOptions = availableRates
    ?.filter(rate => {
      if (currentConfig?.displayRate?.id === rate.id) return true;
      const rateSupportsDisplay = supportsDisplay(rate.data());
      return rateSupportsDisplay && !rate.data().archived;
    })
    .map(rate => ({
      value: rate.id,
      label: rate.data()?.description
    }));

  return (
    <div className="space-y-2 mb-3">
      <GridInput fullWidth>
        <h5 className="text-md font-medium text-column-gray-700">
          {filingType.modelData.label}
        </h5>
      </GridInput>

      <ColumnSelect
        labelText="Liner rate"
        size="sm"
        id={`${filingType.id}-liner-rate`}
        value={currentConfig?.linerRate?.id || ''}
        options={[
          { value: '', label: 'Use default rate' },
          ...linerRateOptions
        ]}
        onChange={newValue => {
          const selectedRate = newValue
            ? availableRates?.find(r => r.id === newValue)
            : null;
          onUpdate('liner', selectedRate || null);
        }}
        errorText={
          linerRateIsArchived
            ? 'The selected rate is archived, please select another rate'
            : undefined
        }
      />

      <ColumnSelect
        labelText="Display rate"
        size="sm"
        id={`${filingType.id}-display-rate`}
        value={currentConfig?.displayRate?.id || ''}
        options={[
          { value: '', label: 'Use default rate' },
          ...displayRateOptions
        ]}
        onChange={newValue => {
          const selectedRate = newValue
            ? availableRates?.find(r => r.id === newValue)
            : null;
          onUpdate('display', selectedRate || null);
        }}
        errorText={
          displayRateIsArchived
            ? 'The selected rate is archived, please select another rate'
            : undefined
        }
      />
    </div>
  );
}

export function ProductRatesSettings({
  productType,
  value,
  onChange,
  activeOrganization
}: ProductRatesSettingsProps) {
  const productName = PRODUCT_TO_NAME[productType].singular;
  const [selectedPublishingMedium, setSelectedPublishingMedium] =
    useState<PublishingMedium | null>(null);

  const rateService = new AdRateService(
    getFirebaseContext().adRatesRef(),
    productType
  );

  const { orgRates: rates, loading: orderRatesLoading } = usePublisherOrgRates(
    rateService,
    {
      includeArchived: true,
      product: productType
    }
  );

  const { value: publishingMediums, isLoading: publishingMediumsLoading } =
    useAsyncEffect({
      fetchData: async () => {
        const organizationModel = getModelFromSnapshot(
          OrganizationModel,
          getFirebaseContext(),
          activeOrganization
        );

        const [error, mediums] =
          await organizationModel.fetchAvailablePublishingMediums(productType);

        if (error) throw error;

        if (mediums && mediums.length > 0) {
          setSelectedPublishingMedium(mediums[0]);
        }

        return mediums || [];
      },
      dependencies: [activeOrganization.id, productType],
      errorConfig: {
        service: ColumnService.SETTINGS_MANAGEMENT,
        message:
          'Failed to load publishing mediums for customer rate configuration'
      }
    });

  // Fetch filing types for the specified product
  const { value: filingTypes, isLoading: filingTypesLoading } = useAsyncEffect({
    fetchData: async () => {
      if (!selectedPublishingMedium) {
        return [];
      }

      const organizationModel = getModelFromSnapshot(
        OrganizationModel,
        getFirebaseContext(),
        activeOrganization
      );

      const [error, filingTypes] =
        await organizationModel.fetchFilingTypesForProductMedium({
          product: productType,
          publishingMedium: selectedPublishingMedium
        });

      if (error) throw error;

      return filingTypes || [];
    },
    dependencies: [
      activeOrganization.id,
      productType,
      selectedPublishingMedium
    ],
    errorConfig: {
      service: ColumnService.SETTINGS_MANAGEMENT,
      message: 'Failed to load filing types for customer rate configuration'
    }
  });

  const handleFilingTypeRateUpdate = (
    filingTypeId: string,
    rateType: 'liner' | 'display',
    rateSnap: ESnapshot<AdRate> | null
  ) => {
    const currentFilingTypeRates = { ...(value.filingTypeRates || {}) };
    const currentConfig = currentFilingTypeRates[filingTypeId] || {};
    const updatedConfig = {
      ...currentConfig,
      [rateType === 'liner' ? 'linerRate' : 'displayRate']: rateSnap?.ref
    };

    // Remove empty configs
    if (!updatedConfig.linerRate && !updatedConfig.displayRate) {
      const { [filingTypeId]: _, ...rest } = currentFilingTypeRates;
      onChange({
        ...value,
        filingTypeRates: rest
      });
    } else {
      onChange({
        ...value,
        filingTypeRates: {
          ...currentFilingTypeRates,
          [filingTypeId]: updatedConfig
        }
      });
    }
  };

  if (filingTypesLoading || publishingMediumsLoading || orderRatesLoading) {
    return <LoadingSpinner />;
  }

  if (!filingTypes || filingTypes.length === 0) {
    return (
      <GridInput fullWidth>
        <Alert
          id="no-filing-types-alert"
          status="info"
          description={`No ${productName.toLowerCase()} categories are configured for this publisher.`}
        />
      </GridInput>
    );
  }

  return (
    <>
      {publishingMediums && publishingMediums.length > 0 && (
        <GridInput fullWidth>
          <h3 className="font-bold mb-3">Default Rates</h3>
          <ColumnSelect
            labelText="Publishing Medium"
            id="publishing-medium-select"
            value={selectedPublishingMedium || ''}
            options={publishingMediums.map(medium => ({
              value: medium,
              label: medium
            }))}
            onChange={newValue => setSelectedPublishingMedium(newValue)}
          />
        </GridInput>
      )}

      {filingTypes.map(filingType => (
        <FilingTypeRateConfig
          key={filingType.id}
          filingType={filingType}
          currentConfig={value.filingTypeRates?.[filingType.id]}
          availableRates={rates}
          onUpdate={(rateType, rateSnap) =>
            handleFilingTypeRateUpdate(filingType.id, rateType, rateSnap)
          }
        />
      ))}
    </>
  );
}
